import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Users, Shield, Clock, ArrowRight, Sparkles, CheckCircle, Search } from 'lucide-react';
import { supabase } from '../config/supabase';
import { ServiceCategory } from '../types';
import { getCategoryImage } from '../utils/categoryImages';
import PWAInstaller from '../components/PWAInstaller';
import { getLaunchPeriodStatus, LaunchPeriodStatus as LaunchPeriodStatusType } from '../utils/launchPeriodHelper';

const HomePage: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ServiceCategory[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [launchPeriodStatus, setLaunchPeriodStatus] = useState<LaunchPeriodStatusType | null>(null);
  const [categoryProviderCounts, setCategoryProviderCounts] = useState<Record<string, number>>({});
  const [stats, setStats] = useState({
    totalProviders: 0,
    averageRating: 0,
    totalReviews: 0,
    totalBookings: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchStats();
    fetchLaunchPeriodStatus();
    fetchCategoryProviderCounts();
  }, []);

  useEffect(() => {
    // Search functionality
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
    } else {
      const results = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(results);
      setShowSearchResults(true);
    }
  }, [searchTerm, categories]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLaunchPeriodStatus = async () => {
    try {
      const status = await getLaunchPeriodStatus();
      setLaunchPeriodStatus(status);
    } catch (error) {
      console.error('Error fetching launch period status:', error);
    }
  };

  const fetchCategoryProviderCounts = async () => {
    try {
      // Get launch period status
      const launchStatus = await getLaunchPeriodStatus();

      // Fetch all active providers
      const { data: providersData, error: providersError } = await supabase
        .from('providers')
        .select('id, selected_service_id, is_active')
        .eq('is_active', true);

      if (providersError) throw providersError;

      const counts: Record<string, number> = {};

      if (providersData) {
        for (const provider of providersData) {
          let shouldInclude = false;

          // During launch period, include ALL active providers
          if (launchStatus?.is_active) {
            shouldInclude = true;
          } else {
            // Normal mode: only providers with active subscriptions
            const { data: activeSubscription } = await supabase
              .from('provider_subscriptions')
              .select('id, price_fcfa, expires_at')
              .eq('provider_id', provider.id)
              .eq('status', 'active')
              .limit(1)
              .maybeSingle();
            
            // Include if subscription is active AND (not expired OR is free)
            if (activeSubscription && 
                (new Date(activeSubscription.expires_at) > new Date() || activeSubscription.price_fcfa === 0)) {
              shouldInclude = true;
            }
          }

          if (shouldInclude && provider.selected_service_id) {
            counts[provider.selected_service_id] = (counts[provider.selected_service_id] || 0) + 1;
          }
        }
      }

      setCategoryProviderCounts(counts);
    } catch (error) {
      console.error('Error fetching category provider counts:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch total active providers (with active account AND active subscription OR launch period active)
      const { data: providersData, error: providersError } = await supabase
        .from('providers')
        .select('id, rating, total_reviews, is_active')
        .eq('is_active', true);

      if (providersError) throw providersError;

      // Get launch period status
      const launchStatus = await getLaunchPeriodStatus();

      // Filter to only count providers with active subscriptions (including free ones) OR during launch period
      let providersWithSubscriptions = 0;
      let totalRatingPoints = 0;
      let totalReviews = 0;

      if (providersData) {
        for (const provider of providersData) {
          let shouldInclude = false;

          // During launch period, include ALL providers (even without subscriptions)
          if (launchStatus?.is_active) {
            shouldInclude = true;
          } else {
            // Normal mode: only providers with active subscriptions
            const { data: activeSubscription } = await supabase
              .from('provider_subscriptions')
              .select('id, price_fcfa, expires_at')
              .eq('provider_id', provider.id)
              .eq('status', 'active')
              .limit(1)
              .maybeSingle();
            
            // Include if subscription is active AND (not expired OR is free)
            if (activeSubscription && 
                (new Date(activeSubscription.expires_at) > new Date() || activeSubscription.price_fcfa === 0)) {
              shouldInclude = true;
            }
          }

          if (shouldInclude) {
            providersWithSubscriptions++;
            if (provider.total_reviews > 0) {
              totalRatingPoints += provider.rating * provider.total_reviews;
              totalReviews += provider.total_reviews;
            }
          }
        }
      }

      // Fetch total bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('id');

      if (bookingsError) throw bookingsError;

      // Calculate stats
      const totalProviders = providersWithSubscriptions;
      const totalBookings = bookingsData?.length || 0;
      const averageRating = totalReviews > 0 ? totalRatingPoints / totalReviews : 0;

      setStats({
        totalProviders,
        averageRating,
        totalReviews,
        totalBookings
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearchSelect = (category: ServiceCategory) => {
    navigate(`/service/${category.slug}`);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      handleSearchSelect(searchResults[0]);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statsDisplay = [
    { icon: Users, label: 'Prestataires vérifiés', value: `${stats.totalProviders}+`, color: 'text-blue-600' },
    { icon: Star, label: 'Note moyenne', value: stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)}/5` : '4.5/5', color: 'text-primary-600' },
    { icon: Shield, label: 'Services sécurisés', value: '100%', color: 'text-green-600' },
    { icon: Clock, label: 'Disponibilité', value: '24h/7j', color: 'text-purple-600' }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Prestataires vérifiés',
      description: 'Tous nos prestataires sont vérifiés et assurés pour votre sécurité'
    },
    {
      icon: Clock,
      title: 'Réservation rapide',
      description: 'Réservez en quelques clics et recevez une confirmation immédiate'
    },
    {
      icon: Star,
      title: 'Qualité garantie',
      description: 'Services de qualité avec un système de notation transparent'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 gradient-gold-radial rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 gradient-gold-radial rounded-full opacity-5 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="animate-slide-in-left">
              {/* CasaPresta Title at the top */}
              <div className="text-center mb-8">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-script font-bold text-gradient-gold mb-4">
                  CasaPresta
                </h1>
              </div>
              
              <div className="flex items-center space-x-2 mb-6">
                <Sparkles className="h-6 w-6 text-primary-500" />
                <span className="text-primary-600 font-semibold text-sm tracking-wide uppercase">
                  Services à Domicile
                </span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-script font-bold text-gray-900 mb-8 leading-tight">
               La plateforme des services à
                <span className="text-gradient-gold block">
                  DOMICILE
                </span>
              </h2>
              
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                Trouvez facilement des professionnels qualifiés pour tous vos besoins à domicile. 
                Réservez en quelques clics et profitez d'un service de qualité premium.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearchSubmit} className="relative mb-8">
                <input
                  type="text"
                  placeholder="Que recherchez-vous ?"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field text-lg h-16 shadow-professional w-full"
                />
                
                {/* Search Results Dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-2 z-10 max-h-60 overflow-y-auto">
                    {searchResults.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleSearchSelect(category)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center"
                      >
                        <img
                          src={category.image_url || getCategoryImage(category.name)}
                          alt={category.name}
                          className="w-10 h-10 rounded-lg object-cover mr-3"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{category.name}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{category.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </form>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={() => {
                    const servicesSection = document.getElementById('services');
                    if (servicesSection) {
                      servicesSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  className="btn-primary inline-flex items-center justify-center group"
                >
                  Découvrir nos services
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link 
                  to="/register" 
                  className="btn-outline inline-flex items-center justify-center hidden lg:flex"
                >
                  Devenir prestataire
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-600">Prestataires vérifiés</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-600">Paiement sécurisé</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-600">Support 24/7</span>
                </div>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="animate-slide-in-right">
              <div className="relative">
                <div className="glass-gold rounded-3xl p-8 shadow-gold">
                  <img
                    src="/images/casa.png"
                    alt="Services à domicile CasaPresta"
                    className="rounded-3xl shadow-professional-lg w-full h-auto"
                    loading="eager"
                    fetchPriority="high"
                  />
                  
                  {/* Floating stats cards */}
                  <div className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-professional animate-pulse-gold">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 gradient-gold rounded-xl flex items-center justify-center">
                        <Star className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)}/5` : '4.5/5'}
                        </p>
                        <p className="text-xs text-gray-500">Note moyenne</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl p-4 shadow-professional animate-pulse-gold" style={{ animationDelay: '1s' }}>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 gradient-gold rounded-xl flex items-center justify-center">
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{stats.totalProviders}+</p>
                        <p className="text-xs text-gray-500">Prestataires</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* PWA Installer removed from here - now in separate section */}
            </div>
          </div>
        </div>
      </section>

      {/* PWA Installer Section - Between hero and stats */}
      <section className="py-8 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <PWAInstaller size="large" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statsDisplay.map((stat, index) => (
              <div key={index} className="text-center animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className={`w-16 h-16 ${stat.color} bg-opacity-10 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl lg:text-5xl font-script font-bold text-gray-900 mb-6">
              Nos <span className="text-gradient-gold">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez notre large gamme de services à domicile, 
              assurés par des professionnels qualifiés et vérifiés.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-professional animate-pulse">
                  <div className="w-16 h-16 bg-gray-200 rounded-2xl mb-6"></div>
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredCategories.map((category, index) => (
                <Link
                  key={category.id}
                  to={`/service/${category.slug}`}
                  className="group bg-white rounded-2xl shadow-professional hover:shadow-gold transition-all duration-500 card-hover animate-scale-in border border-gray-100 hover:border-primary-200 overflow-hidden"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-primary-100 to-primary-200 relative">
                    <img
                      src={category.image_url || getCategoryImage(category.name)}
                      alt={category.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                    />
                    {/* Provider count badge */}
                    <div className="absolute top-3 right-3 bg-white bg-opacity-95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg flex items-center space-x-1.5">
                      <Users className="h-4 w-4 text-primary-600" />
                      <span className="text-sm font-bold text-gray-900">
                        {categoryProviderCounts[category.id] || 0}
                      </span>
                      <span className="text-xs text-gray-600">
                        {(categoryProviderCounts[category.id] || 0) > 1 ? 'pros' : 'pro'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {category.name}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed mb-4 line-clamp-2">
                      {category.description}
                    </p>
                    
                    <div className="flex items-center text-primary-600 font-semibold group-hover:translate-x-2 transition-transform duration-300">
                      <span>Découvrir</span>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How it works Section */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl lg:text-5xl font-script font-bold text-gray-900 mb-6">
              Comment ça <span className="text-gradient-gold">marche</span> ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un processus simple et sécurisé pour réserver vos services à domicile
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div 
              className="text-center animate-slide-in-left"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="w-20 h-20 gradient-gold rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-gold relative">
                <Search className="h-10 w-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Choisissez votre service
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Parcourez nos catégories et trouvez le service qui correspond parfaitement à vos besoins
              </p>
            </div>

            <div 
              className="text-center animate-slide-in-left"
              style={{ animationDelay: '0.4s' }}
            >
              <div className="w-20 h-20 gradient-gold rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-gold relative">
                <Users className="h-10 w-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Sélectionnez un professionnel
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Consultez les profils, avis clients et disponibilités des prestataires vérifiés
              </p>
            </div>

            <div 
              className="text-center animate-slide-in-left"
              style={{ animationDelay: '0.6s' }}
            >
              <div className="w-20 h-20 gradient-gold rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-gold relative">
                <Clock className="h-10 w-10 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  3
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Réservez en ligne
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Choisissez votre créneau, confirmez votre réservation et recevez une confirmation immédiate
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl lg:text-5xl font-script font-bold text-gray-900 mb-6">
              Pourquoi choisir <span className="text-gradient-gold">CasaPresta</span> ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une plateforme conçue pour vous offrir la meilleure expérience 
              de services à domicile en Côte d'Ivoire.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="text-center animate-slide-in-left"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="w-20 h-20 gradient-gold rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-gold">
                  <feature.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section for Professionals */}
      <section className="py-24 gradient-gold relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in">
            <h2 className="text-4xl lg:text-5xl font-script font-bold text-white mb-6">
              Vous êtes un professionnel ?
            </h2>
            <p className="text-xl text-white text-opacity-90 mb-10 max-w-2xl mx-auto">
              Rejoignez notre réseau de prestataires premium et développez votre activité 
              avec des clients de qualité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hidden lg:inline-flex items-center justify-center"
              >
                Devenir prestataire
              </Link>
              <button 
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                Découvrir nos services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;